/**
 * Native offline indicator for the Capacitor shell.
 *
 * The shell loads the live web app over the network, so when connectivity drops
 * there's no native cue (unlike a real offline-aware app). This shows a small
 * themed banner while offline and removes it on reconnect.
 *
 * The banner is injected as a plain DOM element (not React) with inline styles,
 * so it works even if the React app hasn't mounted (e.g. the network dropped
 * during the initial load). No-op outside the Capacitor shell (browser +
 * MobiLoud keep their own behavior) and never throws.
 *
 * @capacitor/network is loaded lazily (dynamic import) so it is excluded from
 * the main entry bundle and never downloaded/parsed in MobiLoud or plain web —
 * only fetched when isCapacitorEnvironment() is true.
 */

import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment } from "./detection";

const BANNER_ID = "tkc-offline-banner";

function reportOfflineError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_offline" },
    });
  } catch {
    // reporting must never break the app
  }
}

// A neutral dark bar (MUI-snackbar style) so it's legible in both light and
// dark themes without having to track the theme. Pinned to the bottom, above
// the home-indicator safe area.
function ensureBanner() {
  const existing = document.getElementById(BANNER_ID);
  if (existing) {
    return existing;
  }
  const el = document.createElement("div");
  el.id = BANNER_ID;
  el.textContent = "You're offline";
  el.setAttribute("role", "status");
  el.style.cssText = [
    "position:fixed",
    "left:0",
    "right:0",
    "bottom:0",
    "z-index:2147483647",
    "padding:10px 12px calc(10px + env(safe-area-inset-bottom))",
    "text-align:center",
    "color:#fff",
    "background:#323232",
    "font:600 14px/1.3 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
    "box-shadow:0 -1px 6px rgba(0,0,0,0.25)",
  ].join(";");
  document.body.appendChild(el);
  return el;
}

function showOfflineBanner() {
  ensureBanner().style.display = "block";
}

function hideOfflineBanner() {
  const el = document.getElementById(BANNER_ID);
  if (el) {
    el.style.display = "none";
  }
}

// Reflect a status object from @capacitor/network: hide when connected, show
// the banner when not.
function applyStatus(status) {
  if (status && status.connected) {
    hideOfflineBanner();
  } else {
    showOfflineBanner();
  }
}

let watching = false;

/**
 * Start watching connectivity and toggling the offline banner. No-op off-shell;
 * runs once; never throws.
 */
export function initOfflineWatch() {
  if (!isCapacitorEnvironment() || watching) {
    return;
  }
  watching = true;
  import("@capacitor/network")
    .then(({ Network }) => {
      // Reflect the current status on launch (offline-at-start shows the banner).
      Network.getStatus().then(applyStatus).catch(reportOfflineError);
      // Network.addListener is async; surface a rejection instead of leaving it as
      // an unhandled promise rejection. A sync throw resets the guard to retry.
      try {
        const result = Network.addListener("networkStatusChange", applyStatus);
        if (result && typeof result.catch === "function") {
          result.catch(reportOfflineError);
        }
      } catch (error) {
        watching = false;
        reportOfflineError(error);
      }
    })
    .catch((error) => {
      watching = false;
      reportOfflineError(error);
    });
}
